/**
 * useVoucherValidation Hook
 * Validates vouchers before order submission
 */

import { useCallback, useState } from 'react';

import { validateVouchers } from '../../queries/voucher';
import { useAuthStore, useShipToStore } from '../../stores';
import type { AppliedVoucher } from '../../stores/useVoucherStore';

export interface VoucherValidationResult {
  isValid: boolean;
  invalidVoucher: AppliedVoucher | null;
}

interface UseVoucherValidationReturn {
  validateVoucher: (
    voucher: AppliedVoucher,
    orderMetaId: string
  ) => Promise<VoucherValidationResult>;
  isValidating: boolean;
  error: Error | null;
}

/**
 * Hook for validating vouchers before order submission
 */
export function useVoucherValidation(): UseVoucherValidationReturn {
  const token = useAuthStore((state) => state.access_token);
  const userId = useAuthStore((state) => state.user?.id ?? '');
  const warehouseId = useShipToStore((state) => state.selectedShipToId);

  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const validateVoucher = useCallback(
    async (
      voucher: AppliedVoucher,
      orderMetaId: string
    ): Promise<VoucherValidationResult> => {
      setIsValidating(true);
      setError(null);

      try {
        const response = await validateVouchers(token, {
          vouchers: [{ id: voucher.id }],
          user_id: userId,
          warehouse_id: warehouseId,
          order_meta_id: orderMetaId,
        });

        const validationResult = response.data.find(
          (item) => item.voucher_id === voucher.id
        );

        const isValid = validationResult?.is_valid ?? false;

        return {
          isValid,
          invalidVoucher: isValid ? null : voucher,
        };
      } catch (err) {
        const errorInstance =
          err instanceof Error ? err : new Error('Voucher validation failed');
        setError(errorInstance);

        // On error, treat voucher as invalid to be safe
        return {
          isValid: false,
          invalidVoucher: voucher,
        };
      } finally {
        setIsValidating(false);
      }
    },
    [token, userId, warehouseId]
  );

  return {
    validateVoucher,
    isValidating,
    error,
  };
}
